import { useRef } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Select, SelectSection, SelectItem } from "@heroui/select";
import { Button } from "@heroui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";

function SectionedSelectInput({
    name,
    label,
    groups,
    placeholder,
    required,
    description,
    size,
    onAutosave,
    showClearButton,
    clearButtonLabel,
}: {
    name: string;
    label: string;
    groups: {
        value?: string | number;
        label: string;
        options: {
            label: string;
            value: string | number;
        }[];
    }[];
    placeholder?: string;
    required?: boolean;
    description?: string;
    size?: "sm" | "md" | "lg";
    onAutosave?: () => void;
    showClearButton?: boolean;
    clearButtonLabel?: string;
}) {
    const { control } = useFormContext();
    const valueChangedRef = useRef(false);

    return (
        <Controller
            name={name}
            control={control}
            render={({
                field: { onBlur, onChange, value },
                fieldState: { error, invalid },
            }) => (
                <Select
                    onChange={(e) => {
                        valueChangedRef.current = true;
                        if (e.target.value === "") {
                            onChange(null);
                        } else {
                            onChange(e.target.value);
                        }
                    }}
                    onBlur={onBlur}
                    onClose={() => {
                        if (onAutosave && valueChangedRef.current) {
                            valueChangedRef.current = false;
                            onAutosave();
                        }
                    }}
                    name={name}
                    isInvalid={invalid}
                    errorMessage={error?.message}
                    label={label}
                    aria-label="Select"
                    selectedKeys={value ? [String(value)] : []}
                    placeholder={placeholder}
                    disabledKeys={["no-options"]}
                    isRequired={required}
                    description={description}
                    size={size}
                    endContent={
                        showClearButton &&
                        !!value && (
                            <Button
                                variant="light"
                                radius="full"
                                size="sm"
                                color="danger"
                                onPress={() => {
                                    onChange(undefined);
                                    if (onAutosave) onAutosave();
                                }}
                            >
                                <FontAwesomeIcon icon={faXmark} />
                                {clearButtonLabel}
                            </Button>
                        )
                    }
                >
                    {groups.map((group, groupIndex) => (
                        <SelectSection
                            key={`${group.label}-${groupIndex}`}
                            title={group.label}
                            className="font-bold"
                        >
                            {group.options.length ? (
                                group.options.map((option) => (
                                    <SelectItem
                                        key={`${option.value}-${group.value}`}
                                    >
                                        {option.label}
                                    </SelectItem>
                                ))
                            ) : (
                                <SelectItem key="no-options">
                                    No options available
                                </SelectItem>
                            )}
                        </SelectSection>
                    ))}
                </Select>
            )}
        />
    );
}

export default SectionedSelectInput;
